import { z } from 'zod'
import { MaintenancePlanPaymentInterval } from '../../contracts/MaintenancePlanContracts'
import { EquipmentType } from '../Equipment/EquipmentType'
import { MaintenancePlanStatus } from '../MaintenancePlans/MaintenancePlanTypes'
import { VISIT_STATUS_TYPES, VisitStatus } from '../MaintenancePlans/Visits'
import { BaseCsvExportRequest, baseCsvExportRequestSchema } from './CsvExportTypes'

export const MAINTENANCE_PLANS_CSV_EXPORT_FIELD_IDS = [
  'maintenancePlanGuid',
  'planName',
  'status',
  'paymentFlow',
  'paymentInterval',
  'activatedAt',
  'lapsedAt',
  'terminatesAt',
  'daysSinceActivation',
  'planAgeMonths',
  'accountName',
  'primaryContactName',
  'primaryContactPhone',
  'primaryContactEmail',
  'paymentAmountUsc',
  'paymentAmountUsd',
  'annualValueUsd',
  'mrrUsd',
  'lifetimeRevenueUsd',
  'isAutoRenewing',
  'renewalDate',
  'daysUntilRenewal',
  'unusedVisitsCount',
  'totalVisitsCount',
  'completedVisitsCount',
  'overdueVisitsCount',
  'nextVisitDate',
  'nextVisitStatus',
  'daysUntilNextVisit',
  'daysOverdueNextVisit',
  'locationGuid',
  'locationAddress',
  'locationZipCode',
  'equipmentTypes',
  'equipmentCount',
  'soldByUserGuid',
  'soldByName',
  'businessUnitName',
  'accountTags',
] as const

export type MaintenancePlansCsvExportFieldId = (typeof MAINTENANCE_PLANS_CSV_EXPORT_FIELD_IDS)[number]

export type MaintenancePlanCsvExportFieldGroupId =
  | 'PLAN'
  | 'ACCOUNT'
  | 'FINANCIAL'
  | 'RENEWAL'
  | 'VISITS'
  | 'LOCATION'
  | 'EQUIPMENT'
  | 'SALES'
  | 'TAGS'

export type MaintenancePlanCsvExportFieldDefinition = {
  id: MaintenancePlansCsvExportFieldId
  label: string
  description?: string
  groupId: MaintenancePlanCsvExportFieldGroupId
  defaultSelected: boolean
}

export const maintenancePlansCsvExportFieldDefinitions: Record<
  MaintenancePlansCsvExportFieldId,
  MaintenancePlanCsvExportFieldDefinition
> = {
  maintenancePlanGuid: {
    id: 'maintenancePlanGuid',
    label: 'Plan GUID',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  planName: {
    id: 'planName',
    label: 'Plan Name',
    groupId: 'PLAN',
    defaultSelected: true,
  },
  status: {
    id: 'status',
    label: 'Plan Status',
    groupId: 'PLAN',
    defaultSelected: true,
  },
  paymentFlow: {
    id: 'paymentFlow',
    label: 'Payment Flow',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  paymentInterval: {
    id: 'paymentInterval',
    label: 'Payment Interval',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  activatedAt: {
    id: 'activatedAt',
    label: 'Activated At',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  lapsedAt: {
    id: 'lapsedAt',
    label: 'Lapsed At',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  terminatesAt: {
    id: 'terminatesAt',
    label: 'Terminates At',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  daysSinceActivation: {
    id: 'daysSinceActivation',
    label: 'Days Since Activation',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  planAgeMonths: {
    id: 'planAgeMonths',
    label: 'Plan Age (Months)',
    groupId: 'PLAN',
    defaultSelected: false,
  },
  accountName: {
    id: 'accountName',
    label: 'Account Name',
    groupId: 'ACCOUNT',
    defaultSelected: true,
  },
  primaryContactName: {
    id: 'primaryContactName',
    label: 'Primary Contact Name',
    groupId: 'ACCOUNT',
    defaultSelected: true,
  },
  primaryContactPhone: {
    id: 'primaryContactPhone',
    label: 'Primary Contact Phone',
    groupId: 'ACCOUNT',
    defaultSelected: true,
  },
  primaryContactEmail: {
    id: 'primaryContactEmail',
    label: 'Primary Contact Email',
    groupId: 'ACCOUNT',
    defaultSelected: false,
  },
  paymentAmountUsc: {
    id: 'paymentAmountUsc',
    label: 'Payment Amount (USC)',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
  paymentAmountUsd: {
    id: 'paymentAmountUsd',
    label: 'Payment Amount (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: true,
  },
  annualValueUsd: {
    id: 'annualValueUsd',
    label: 'Annual Value (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: true,
  },
  mrrUsd: {
    id: 'mrrUsd',
    label: 'MRR (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: true,
  },
  lifetimeRevenueUsd: {
    id: 'lifetimeRevenueUsd',
    label: 'Lifetime Revenue (USD)',
    groupId: 'FINANCIAL',
    defaultSelected: false,
  },
  isAutoRenewing: {
    id: 'isAutoRenewing',
    label: 'Is Auto-Renewing',
    groupId: 'RENEWAL',
    defaultSelected: true,
  },
  renewalDate: {
    id: 'renewalDate',
    label: 'Renewal Date',
    groupId: 'RENEWAL',
    defaultSelected: true,
  },
  daysUntilRenewal: {
    id: 'daysUntilRenewal',
    label: 'Days Until Renewal',
    groupId: 'RENEWAL',
    defaultSelected: false,
  },
  unusedVisitsCount: {
    id: 'unusedVisitsCount',
    label: 'Unused Visits Count',
    groupId: 'VISITS',
    defaultSelected: true,
  },
  totalVisitsCount: {
    id: 'totalVisitsCount',
    label: 'Total Visits Count',
    groupId: 'VISITS',
    defaultSelected: true,
  },
  completedVisitsCount: {
    id: 'completedVisitsCount',
    label: 'Completed Visits Count',
    groupId: 'VISITS',
    defaultSelected: false,
  },
  overdueVisitsCount: {
    id: 'overdueVisitsCount',
    label: 'Overdue Visits Count',
    groupId: 'VISITS',
    defaultSelected: false,
  },
  nextVisitDate: {
    id: 'nextVisitDate',
    label: 'Next Visit Date',
    groupId: 'VISITS',
    defaultSelected: true,
  },
  nextVisitStatus: {
    id: 'nextVisitStatus',
    label: 'Next Visit Status',
    groupId: 'VISITS',
    defaultSelected: true,
  },
  daysUntilNextVisit: {
    id: 'daysUntilNextVisit',
    label: 'Days Until Next Visit',
    groupId: 'VISITS',
    defaultSelected: false,
  },
  daysOverdueNextVisit: {
    id: 'daysOverdueNextVisit',
    label: 'Days Overdue Next Visit',
    groupId: 'VISITS',
    defaultSelected: false,
  },
  locationGuid: {
    id: 'locationGuid',
    label: 'Location GUID',
    groupId: 'LOCATION',
    defaultSelected: false,
  },
  locationAddress: {
    id: 'locationAddress',
    label: 'Location Full Address',
    groupId: 'LOCATION',
    defaultSelected: true,
  },
  locationZipCode: {
    id: 'locationZipCode',
    label: 'Location Zip Code',
    groupId: 'LOCATION',
    defaultSelected: true,
  },
  equipmentTypes: {
    id: 'equipmentTypes',
    label: 'Equipment Types',
    groupId: 'EQUIPMENT',
    defaultSelected: true,
  },
  equipmentCount: {
    id: 'equipmentCount',
    label: 'Equipment Count',
    groupId: 'EQUIPMENT',
    defaultSelected: false,
  },
  soldByUserGuid: {
    id: 'soldByUserGuid',
    label: 'Sold By User GUID',
    groupId: 'SALES',
    defaultSelected: false,
  },
  soldByName: {
    id: 'soldByName',
    label: 'Sold By Name',
    groupId: 'SALES',
    defaultSelected: false,
  },
  businessUnitName: {
    id: 'businessUnitName',
    label: 'Business Unit Name',
    groupId: 'SALES',
    defaultSelected: false,
  },
  accountTags: {
    id: 'accountTags',
    label: 'Account Tags',
    groupId: 'TAGS',
    defaultSelected: false,
  },
}

export const maintenancePlansCsvExportFieldGroups: {
  id: MaintenancePlanCsvExportFieldGroupId
  label: string
  description?: string
  fieldIds: MaintenancePlansCsvExportFieldId[]
}[] = [
  {
    id: 'PLAN',
    label: 'Plan Information',
    fieldIds: [
      'maintenancePlanGuid',
      'planName',
      'status',
      'paymentFlow',
      'paymentInterval',
      'activatedAt',
      'lapsedAt',
      'terminatesAt',
      'daysSinceActivation',
      'planAgeMonths',
    ],
  },
  {
    id: 'ACCOUNT',
    label: 'Account',
    fieldIds: ['accountName', 'primaryContactName', 'primaryContactPhone', 'primaryContactEmail'],
  },
  {
    id: 'FINANCIAL',
    label: 'Financials',
    fieldIds: ['paymentAmountUsc', 'paymentAmountUsd', 'annualValueUsd', 'mrrUsd', 'lifetimeRevenueUsd'],
  },
  {
    id: 'RENEWAL',
    label: 'Renewal',
    fieldIds: ['isAutoRenewing', 'renewalDate', 'daysUntilRenewal'],
  },
  {
    id: 'VISITS',
    label: 'Visits',
    fieldIds: [
      'unusedVisitsCount',
      'totalVisitsCount',
      'completedVisitsCount',
      'overdueVisitsCount',
      'nextVisitDate',
      'nextVisitStatus',
      'daysUntilNextVisit',
      'daysOverdueNextVisit',
    ],
  },
  {
    id: 'LOCATION',
    label: 'Location',
    fieldIds: ['locationGuid', 'locationAddress', 'locationZipCode'],
  },
  {
    id: 'EQUIPMENT',
    label: 'Equipment',
    fieldIds: ['equipmentTypes', 'equipmentCount'],
  },
  {
    id: 'SALES',
    label: 'Sales',
    fieldIds: ['soldByUserGuid', 'soldByName', 'businessUnitName'],
  },
  {
    id: 'TAGS',
    label: 'Tags',
    fieldIds: ['accountTags'],
  },
]

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

export type MonthOption = (typeof MONTHS)[number]

export const EQUIPMENT_AGE_OPTIONS = ['Unknown', '0-2', '3-6', '7-10', '10+'] as const
export type EquipmentAgeOption = (typeof EQUIPMENT_AGE_OPTIONS)[number]

export const VISITS_REMAINING_TYPES = ['NONE', '1_OR_MORE', '2_OR_MORE', 'ANY'] as const
export type VisitsRemainingType = (typeof VISITS_REMAINING_TYPES)[number]

export const AUTO_RENEWING_STATUSES = ['AUTO', 'MANUAL', 'ALL'] as const
export type AutoRenewingStatus = (typeof AUTO_RENEWING_STATUSES)[number]

export const PLAN_RENEWAL_DATE_TYPES = [
  'LAST_3_MONTHS',
  'LAST_MONTH',
  'THIS_MONTH',
  'NEXT_3_MONTHS',
  'ALL_TIME',
] as const
export type PlanRenewalDateType = (typeof PLAN_RENEWAL_DATE_TYPES)[number]

export const maintenancePlansCsvExportFiltersSchema = z.object({
  searchTerm: z.string().optional(),
  nextVisitStatuses: z.array(z.enum(VISIT_STATUS_TYPES)).default([]),
  nextVisitMonths: z.array(z.enum(MONTHS)).default([]),
  equipmentTypes: z.array(z.nativeEnum(EquipmentType)).default([]),
  equipmentAges: z.array(z.enum(EQUIPMENT_AGE_OPTIONS)).default([]),
  zipCodes: z.array(z.string()).default([]),
  tagNames: z.array(z.string()).default([]),
  visitsRemaining: z.enum(VISITS_REMAINING_TYPES).default('ANY'),
  planTypes: z.array(z.string()).default([]),
  planStatuses: z.array(z.nativeEnum(MaintenancePlanStatus)).default([]),
  autoRenewingStatus: z.enum(AUTO_RENEWING_STATUSES).default('ALL'),
  planRenewalDate: z.enum(PLAN_RENEWAL_DATE_TYPES).default('ALL_TIME'),
  soldByNames: z.array(z.string()).default([]),
})

export type MaintenancePlansCsvExportFilters = z.infer<typeof maintenancePlansCsvExportFiltersSchema>

export const maintenancePlansCsvExportRequestSchema = baseCsvExportRequestSchema.extend({
  fieldIds: z.array(z.enum(MAINTENANCE_PLANS_CSV_EXPORT_FIELD_IDS)),
  filters: maintenancePlansCsvExportFiltersSchema,
})

export type MaintenancePlansCsvExportRequest = z.infer<typeof maintenancePlansCsvExportRequestSchema>

export type MaintenancePlansCsvExportRow = {
  maintenancePlanGuid: string
  planName?: string
  status: string
  paymentFlow: string
  paymentInterval?: string
  activatedAt?: string
  lapsedAt?: string
  terminatesAt?: string
  daysSinceActivation?: number
  planAgeMonths?: number
  accountName: string
  primaryContactName?: string
  primaryContactPhone?: string
  primaryContactEmail?: string
  paymentAmountUsc?: number
  paymentAmountUsd?: number
  annualValueUsd?: number
  mrrUsd?: number
  lifetimeRevenueUsd?: number
  isAutoRenewing: boolean
  renewalDate?: string
  daysUntilRenewal?: number
  unusedVisitsCount: number
  totalVisitsCount: number
  completedVisitsCount: number
  overdueVisitsCount: number
  nextVisitDate?: string
  nextVisitStatus?: string
  daysUntilNextVisit?: number
  daysOverdueNextVisit?: number
  locationGuid: string
  locationAddress?: string
  locationZipCode?: string
  equipmentTypes?: string
  equipmentCount: number
  soldByUserGuid?: string
  soldByName?: string
  businessUnitName?: string
  accountTags?: string
  nextVisitAffinityDate?: string
  equipmentInstallationDates?: string[]
  yearlyPriceUsc?: number
}
